import { apiFetch } from "./client";
import { endpoints } from "./endpoints";
import { disableLocalRestaurantPushDelivery } from "../firebase/messaging";

const TOKEN_KEY_PREFIX = "a2z_restaurant_fcm_token_";
const TOKEN_OWNER_KEY_PREFIX = "a2z_restaurant_fcm_owner_";
const PENDING_CLEANUP_KEY = "a2z_restaurant_fcm_pending_cleanup";
const activeRegistrations = new Set<Promise<void>>();
const activeRegistrationAttempts = new Set<Promise<void>>();
let registrationGeneration = 0;
let registrationAllowed = true;

export function activateRestaurantPushSession(): void {
  registrationGeneration += 1;
  registrationAllowed = true;
}

export function runRestaurantPushRegistrationAttempt(
  operation: (isCurrent: () => boolean) => Promise<void>,
): Promise<void> {
  const generation = registrationGeneration;
  const isCurrent = () =>
    registrationAllowed && generation === registrationGeneration;
  const attempt = (async () => {
    if (isCurrent()) await operation(isCurrent);
  })();
  activeRegistrationAttempts.add(attempt);
  void attempt.then(
    () => activeRegistrationAttempts.delete(attempt),
    () => activeRegistrationAttempts.delete(attempt),
  );
  return attempt;
}

async function invalidateAndWaitForRegistrationAttempts(): Promise<void> {
  registrationGeneration += 1;
  registrationAllowed = false;
  await Promise.allSettled([...activeRegistrationAttempts]);
}

function tokenKey(storeId: string): string {
  return `${TOKEN_KEY_PREFIX}${storeId}`;
}

function tokenOwnerKey(storeId: string): string {
  return `${TOKEN_OWNER_KEY_PREFIX}${storeId}`;
}

type StoredTokenEntry = {
  key: string;
  storeId: string;
  token: string;
  ownerId: string | null;
};

type PendingCleanupEntry = {
  token: string;
  ownerId: string | null;
};

function storedEntries(): StoredTokenEntry[] {
  const entries: StoredTokenEntry[] = [];
  for (let index = 0; index < localStorage.length; index += 1) {
    const key = localStorage.key(index);
    if (!key?.startsWith(TOKEN_KEY_PREFIX)) continue;
    const token = localStorage.getItem(key);
    if (!token) continue;
    const storeId = key.slice(TOKEN_KEY_PREFIX.length);
    entries.push({
      key,
      storeId,
      token,
      ownerId: localStorage.getItem(tokenOwnerKey(storeId)),
    });
  }
  return entries;
}

function clearStoredToken(token: string, ownerId?: string): void {
  for (const entry of storedEntries()) {
    if (
      entry.token === token &&
      (!ownerId || !entry.ownerId || entry.ownerId === ownerId)
    ) {
      localStorage.removeItem(entry.key);
      localStorage.removeItem(tokenOwnerKey(entry.storeId));
    }
  }
}

function pendingCleanupEntries(): PendingCleanupEntry[] {
  try {
    const parsed = JSON.parse(localStorage.getItem(PENDING_CLEANUP_KEY) ?? "[]");
    if (!Array.isArray(parsed)) return [];
    const entries = parsed.flatMap((value): PendingCleanupEntry[] => {
      if (typeof value === "string") return [{ token: value, ownerId: null }];
      if (
        typeof value === "object" &&
        value !== null &&
        "token" in value &&
        typeof value.token === "string"
      ) {
        return [{
          token: value.token,
          ownerId:
            "ownerId" in value && typeof value.ownerId === "string"
              ? value.ownerId
              : null,
        }];
      }
      return [];
    });
    return entries.filter(
      (entry, index) =>
        entries.findIndex(
          (candidate) =>
            candidate.token === entry.token &&
            candidate.ownerId === entry.ownerId,
        ) === index,
    );
  } catch {
    return [];
  }
}

function writePendingCleanup(entries: readonly PendingCleanupEntry[]): void {
  const unique = entries.filter(
    (entry, index) =>
      entries.findIndex(
        (candidate) =>
          candidate.token === entry.token &&
          candidate.ownerId === entry.ownerId,
      ) === index,
  );
  if (unique.length === 0) {
    localStorage.removeItem(PENDING_CLEANUP_KEY);
    return;
  }
  localStorage.setItem(PENDING_CLEANUP_KEY, JSON.stringify(unique));
}

function enqueueCleanup(token: string, ownerId: string): void {
  writePendingCleanup([
    ...pendingCleanupEntries(),
    { token, ownerId },
  ]);
}

async function removeTokenFromBackend(token: string): Promise<void> {
  await apiFetch<void>(endpoints.seller.fcmTokens, {
    method: "DELETE",
    body: { token },
  });
}

/** Retry rotation cleanup without forgetting any token whose DELETE still fails. */
export async function retryRestaurantFcmTokenCleanup(
  ownerId?: string,
  currentToken?: string,
): Promise<void> {
  const pending = pendingCleanupEntries();
  if (pending.length === 0) return;
  const retained: PendingCleanupEntry[] = [];
  for (const entry of pending) {
    if (currentToken && entry.token === currentToken) continue;
    if (ownerId && entry.ownerId && entry.ownerId !== ownerId) {
      retained.push(entry);
      continue;
    }
    try {
      await removeTokenFromBackend(entry.token);
      clearStoredToken(entry.token, ownerId);
    } catch (error) {
      retained.push(entry);
      console.warn(
        "[restaurant-notifications] Rotated FCM token cleanup failed",
        error,
      );
    }
  }
  writePendingCleanup(retained);
}

export function registerRestaurantFcmToken(
  token: string,
  storeId: string,
  ownerId: string,
): Promise<void> {
  const operation = (async () => {
    const previousToken = localStorage.getItem(tokenKey(storeId));
    const previousOwner = localStorage.getItem(tokenOwnerKey(storeId));
    await apiFetch<void>(endpoints.seller.fcmTokens, {
      method: "POST",
      body: { token },
    });
    // The replacement is durable before the old token is touched. A failed POST
    // therefore leaves both backend delivery and the local current-token pointer
    // on the old token.
    localStorage.setItem(tokenKey(storeId), token);
    localStorage.setItem(tokenOwnerKey(storeId), ownerId);
    if (
      previousToken &&
      previousToken !== token &&
      (!previousOwner || previousOwner === ownerId)
    ) {
      enqueueCleanup(previousToken, ownerId);
    }
    // A successful POST makes this token current and atomically evicts the same
    // token from every prior SELLER owner. Never DELETE it as stale cleanup.
    // Other prior-owner pointers cannot be deleted with this owner's cookie and
    // are safe to retire locally only after the new registration succeeds.
    writePendingCleanup(
      pendingCleanupEntries().filter(
        (entry) => entry.token !== token && (!entry.ownerId || entry.ownerId === ownerId),
      ),
    );
    await retryRestaurantFcmTokenCleanup(ownerId, token);
  })();
  activeRegistrations.add(operation);
  void operation.then(
    () => activeRegistrations.delete(operation),
    () => activeRegistrations.delete(operation),
  );
  return operation;
}

async function waitForActiveRegistrations(): Promise<void> {
  await Promise.allSettled([...activeRegistrations]);
}

/**
 * The existing seller DELETE endpoint removes this token for the authenticated
 * owner across every store. Mirror that backend scope locally: once it
 * succeeds, every restaurant-store cache entry carrying the token is removed.
 */
export async function deregisterRestaurantFcmToken(
  storeId: string,
  ownerId?: string,
): Promise<void> {
  const entry = storedEntries().find((candidate) => candidate.storeId === storeId);
  if (!entry || (ownerId && entry.ownerId && entry.ownerId !== ownerId)) return;
  await removeTokenFromBackend(entry.token);
  clearStoredToken(entry.token, ownerId);
}

/** Remove every distinct browser token while the authenticated cookie exists. */
export async function deregisterAllRestaurantFcmTokens(
  ownerId?: string,
): Promise<void> {
  const untouched = pendingCleanupEntries().filter(
    (entry) => ownerId && entry.ownerId && entry.ownerId !== ownerId,
  );
  const candidates = [
    ...storedEntries()
      .filter((entry) => !ownerId || !entry.ownerId || entry.ownerId === ownerId)
      .map((entry) => ({ token: entry.token, ownerId: ownerId ?? entry.ownerId })),
    ...pendingCleanupEntries().filter(
      (entry) => !ownerId || !entry.ownerId || entry.ownerId === ownerId,
    ),
  ];
  const tokens = [...new Set(candidates.map((entry) => entry.token))];
  const results = await Promise.allSettled(
    tokens.map(async (token) => {
      await removeTokenFromBackend(token);
      clearStoredToken(token, ownerId);
      return token;
    }),
  );
  const failed = results.flatMap((result, index): PendingCleanupEntry[] =>
    result.status === "rejected"
      ? [{ token: tokens[index], ownerId: ownerId ?? null }]
      : [],
  );
  writePendingCleanup([...untouched, ...failed]);
  if (failed.length > 0) {
    throw new Error(`Failed to deregister ${failed.length} restaurant FCM token(s)`);
  }
}

/** Clear visible current-owner pointers on session-owner replacement. */
export function clearStoredRestaurantFcmTokens(): void {
  for (const entry of storedEntries()) {
    localStorage.removeItem(entry.key);
    localStorage.removeItem(tokenOwnerKey(entry.storeId));
  }
}

export async function clearStoredRestaurantFcmTokensAfterRegistrations(): Promise<void> {
  await waitForActiveRegistrations();
  clearStoredRestaurantFcmTokens();
}

export async function prepareRestaurantNotificationLogout(
  ownerId?: string,
): Promise<void> {
  try {
    // A POST already on the wire cannot be reliably aborted: the backend may
    // commit even if fetch rejects AbortError. Let it settle first, then DELETE
    // every resulting local token so nothing can re-register after cleanup.
    await invalidateAndWaitForRegistrationAttempts();
    await waitForActiveRegistrations();
    await deregisterAllRestaurantFcmTokens(ownerId);
  } catch (error) {
    console.warn(
      "[restaurant-notifications] Logout token deregistration failed",
      error,
    );
    // The owner-tagged retry pointer remains durable for a later authenticated
    // session, but this shared device must stop receiving immediately.
    await disableLocalRestaurantPushDelivery();
  }
}

export function getStoredRestaurantFcmToken(storeId: string): string | null {
  return localStorage.getItem(tokenKey(storeId));
}
